import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { App } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";

import QueryTable from "@/components_v2/Tables/QueryTable";
import { OrganizationRoles, Roles } from "@/shared/utils/constants";
import {
  InviteFiltersState,
  InviteTableProps,
} from "@/features/organizations/profile/types";
import {
  useDeleteUserInviteByIdMutation,
  useLazyGetUserInvitesQuery,
} from "@/redux/service";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import BaseButton from "@/components_v2/Button/BaseButton";
import ConfirmationModal from "@/components_v2/Modal/ConfirmationModal";
import { UserInviteResponse } from "@/redux/invites/types";
import { useInvitePermissions } from "@/features/organizations/profile/hooks/useInvitePermissions";
import InviteFormModal from "@/features/organizations/profile/components/InviteFormModal";
import InviteFilters from "@/features/organizations/profile/components/InviteFilters";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import SendIcon from "@/assets/icons/send.svg?react";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "../styles.css";

const UserInviteActionsCell = ({ invite }: { invite: UserInviteResponse }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [deleteUserInviteById] = useDeleteUserInviteByIdMutation();
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const { canEdit, canDelete } = useInvitePermissions(invite, currentUser);

  const handleDeleteUserInviteById = (inviteId: number) => {
    deleteUserInviteById(inviteId)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.inviteDeleted"),
        });
        setOpenConfirmDeleteModal(false);
      });
  };

  const copyRegistrationLinkToClipboard = () => {
    navigator.clipboard.writeText(invite.registrationLink);
    notification.success({
      message: t("organizations.labels.registrationLinkCopied"),
    });
  };

  return (
    currentUser && (
      <>
        <DropdownMenu
          content={
            <div>
              <BaseButton
                className="dropdown-menu-button"
                isFullWidth
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.LINK}
                leadingIcon={<CopyOutlined />}
                onClick={copyRegistrationLinkToClipboard}
              >
                {t("organizations.labels.copyRegistrationLink")}
              </BaseButton>
              {canEdit && (
                <BaseButton
                  className="dropdown-menu-button"
                  isFullWidth
                  variant="link"
                  size="small"
                  leadingIcon={<EditOutlined />}
                  onClick={() => setOpenEditModal(true)}
                >
                  {t("organizations.labels.edit")}
                </BaseButton>
              )}

              {canDelete && (
                <BaseButton
                  className="dropdown-menu-button"
                  isFullWidth
                  variant="link"
                  size="small"
                  leadingIcon={<DeleteOutlined />}
                  onClick={() => setOpenConfirmDeleteModal(true)}
                >
                  {t("organizations.labels.delete")}
                </BaseButton>
              )}
            </div>
          }
          placement="bottom"
        >
          <BaseButton
            variant="link"
            size="small"
            leadingIcon={<EllipsisOutlined />}
          />
        </DropdownMenu>

        <InviteFormModal
          open={openEditModal}
          setOpen={setOpenEditModal}
          defaultData={invite}
        />

        <ConfirmationModal
          open={openConfirmDeleteModal}
          onCancel={() => setOpenConfirmDeleteModal(false)}
          onConfirm={() => handleDeleteUserInviteById(invite.id)}
          title={t("organizations.labels.confirmUserInviteDelete")}
          confirmButtonText={t("organizations.labels.delete")}
          content={t("organizations.labels.confirmDeleteInvite", {
            email: invite.email,
          })}
        />
      </>
    )
  );
};

const UserInviteTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  ...props
}: InviteTableProps<UserInviteResponse>) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const [openCreateInviteModal, setOpenCreateInviteModal] = useState(false);
  const [filters, setFilters] =
    useState<Partial<InviteFiltersState>>(defaultFilters);

  const columns: Record<
    string,
    ColumnType<UserInviteResponse>
  > = useMemo(() => {
    const predefinedColumns: Record<string, ColumnType<UserInviteResponse>> = {
      firstName: {
        title: t("organizations.labels.firstName"),
        key: "firstName",
        dataIndex: "firstName",
      },
      lastName: {
        title: t("organizations.labels.lastName"),
        key: "lastName",
        dataIndex: "lastName",
      },
      email: {
        title: t("organizations.labels.email"),
        key: "email",
        dataIndex: "email",
      },
      role: {
        title: t("organizations.labels.role"),
        dataIndex: "role",
        render: (role: string) => {
          return OrganizationRoles.getItemByValue(role)?.label;
        },
      },
      actions: {
        title: t("organizations.labels.actions"),
        key: "actions",
        render: (invite: UserInviteResponse) => (
          <UserInviteActionsCell invite={invite} />
        ),
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [additionalColumns]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<UserInviteResponse>,
      ),
    [columns, columnNames],
  );

  return (
    currentUser && (
      <div className="table-container-layout">
        <div className="flex flex-col gap-200">
          <div className="self-end">
            <BaseButton
              onClick={() => setOpenCreateInviteModal(true)}
              leadingIcon={<BaseSvgRenderer component={SendIcon} width="20" />}
            >
              {t("organizations.labels.createUserInvite")}
            </BaseButton>
          </div>

          <InviteFilters
            filterNames={filterNames}
            setFilters={setFilters}
            defaultFilters={defaultFilters}
          />
        </div>

        <QueryTable<UserInviteResponse>
          query={useLazyGetUserInvitesQuery}
          filters={filters}
          columns={mappedColumns}
          {...props}
        />

        <InviteFormModal
          open={openCreateInviteModal}
          setOpen={setOpenCreateInviteModal}
          defaultData={{
            organization:
              currentUser.role === Roles.ADMIN.value
                ? (filters.organization as number)
                : currentUser.organization?.id,
          }}
        />
      </div>
    )
  );
};

export default UserInviteTable;
